import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Mail, Search, Trash2, Loader2 } from 'lucide-react';
import { Event } from '../../../services/EventService';
import { EmailService } from '../../../services/EmailService';
import { useAuth } from '../../../contexts/AuthContext';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

interface CoHost {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status: 'pending' | 'accepted' | 'declined';
  invited_at: string;
}

interface CoHostsManagerProps {
  eventId: string;
  coHosts: CoHost[];
  onUpdate: () => void;
}

export const CoHostsManager: React.FC<CoHostsManagerProps> = ({ 
  eventId, 
  coHosts: initialCoHosts, 
  onUpdate 
}) => {
  const { user } = useAuth();
  const [coHosts, setCoHosts] = useState<CoHost[]>(initialCoHosts);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Fetch co-hosts when component mounts
  useEffect(() => {
    fetchCoHosts();
  }, [eventId]);

  const fetchCoHosts = async () => {
    try {
      const { data, error } = await Event.getCoHosts(eventId);
      if (error) {
        setError('Failed to load co-hosts');
        return;
      }
      setCoHosts(data || []);
    } catch (err) {
      setError('An error occurred while loading co-hosts');
    }
  };

  const handleInviteCoHost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !user) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const coHostData = [{ email: inviteEmail.trim() }];
      const { data, error } = await Event.addCoHosts(eventId, coHostData, user.id);

      if (error) {
        setError(error.message || 'Failed to invite co-host.');
        return;
      }

      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      
      // Refresh co-hosts list
      await fetchCoHosts();
      onUpdate();

    } catch (err: any) {
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCoHost = async (coHostId: string) => {
    if (!user) return;
    try {
      const { error } = await Event.removeCoHost(coHostId, user.id);
      if (error) {
        setError('Failed to remove co-host: ' + error.message);
        return;
      }

      setSuccess('Co-host removed successfully');
      await fetchCoHosts();
      onUpdate();
    } catch (err: any) {
      setError('An error occurred: ' + err.message);
    }
  };

  const handleResendInvitation = async (coHost: CoHost) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Generate new token
      const { data: tokenData, error: tokenError } = await EmailService.generateCoHostToken(
        eventId,
        coHost.email,
        coHost.name
      );

      if (tokenError) {
        setError('Failed to generate new invitation: ' + tokenError.message);
        setIsLoading(false);
        return;
      }

      // Send email
      const { error: emailError } = await EmailService.sendCoHostInvitation(
        eventId,
        coHost.email,
        coHost.name,
        tokenData.token
      );

      if (emailError) {
        setError('Failed to resend invitation: ' + emailError.message);
        setIsLoading(false);
        return;
      }

      setSuccess(`Invitation resent to ${coHost.email}`);
    } catch (err: any) {
      setError('An error occurred: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCoHosts = useMemo(() => {
    return coHosts.filter(coHost => {
      const matchesSearch = 
        coHost.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        coHost.email.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = 
        statusFilter === 'all' || coHost.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [coHosts, searchQuery, statusFilter]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Accepted</span>;
      case 'declined':
        return <span className="inline-block px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Declined</span>;
      case 'pending':
        return <span className="inline-block px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Pending</span>;
      default:
        return <span className="inline-block px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Unknown</span>;
    }
  };

  return (
    <div className="space-y-6">


      {/* Invite and Filter Form */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 bg-white p-4 rounded-lg border shadow-sm">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-2">Invite Co-host</h3>
          <form onSubmit={handleInviteCoHost} className="flex space-x-2">
            <Input
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
              required
            />
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || !inviteEmail.trim()}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span className="ml-2">Invite</span>
            </Button>
          </form>
        </div>

        <div className="flex-1">
          <h3 className="font-medium text-gray-900 mb-2">Filter Co-hosts</h3>
          <div className="flex space-x-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
              <Search className="h-4 w-4 absolute left-2.5 top-3 text-gray-400" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-red-500 text-sm mt-1">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-700">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-500 text-sm mt-1">Dismiss</button>
        </div>
      )}

      {/* Co-hosts List */}
      <div className="bg-white rounded-lg border shadow">
        {coHosts.length === 0 ? (
          <div className="p-8 text-center">
            <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No co-hosts yet</h3>
            <p className="text-gray-500 mb-4">Start by inviting someone to help you manage this event.</p>
          </div>
        ) : filteredCoHosts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No co-hosts match your current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Name</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">Invited</th>
                  <th className="text-right py-2 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoHosts.map((coHost) => (
                  <tr key={coHost.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center overflow-hidden">
                          {coHost.avatar ? (
                            <img 
                              src={coHost.avatar} 
                              alt={coHost.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-purple-600">
                              {coHost.name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{coHost.name}</div>
                          <div className="text-sm text-gray-500">{coHost.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {getStatusBadge(coHost.status)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {new Date(coHost.invited_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex justify-end items-center gap-2">
                        {coHost.status === 'pending' && (
                          <button
                            onClick={() => handleResendInvitation(coHost)}
                            disabled={isLoading}
                            className="p-1 text-purple-600 hover:bg-purple-50 rounded-full transition-colors disabled:opacity-50"
                            title="Resend invitation"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveCoHost(coHost.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Remove co-host"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoHostsManager;